
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ProfileImageSection from "@/components/user/ProfileImageSection";
import PersonalInfoForm from "@/components/user/PersonalInfoForm";
import SecuritySection from "@/components/user/SecuritySection";
import MarketsTab from "@/components/user/MarketsTab";
import BillingTab from "@/components/user/BillingTab";
import BuyerPersonalInfoForm from "@/components/buyer/profile/BuyerPersonalInfoForm";
import BuyerInvestmentPreferencesForm from "@/components/buyer/profile/BuyerInvestmentPreferencesForm";
import BuyerProfileImageSection from "@/components/buyer/profile/BuyerProfileImageSection";
import { Skeleton } from "@/components/ui/skeleton";
import { useBuyer } from "@/contexts/BuyerContext";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation } from "react-router-dom";
import ProfileTypeSelector from "@/components/user/ProfileTypeSelector";
import ChangePasswordDialog from "@/components/auth/ChangePasswordDialog";
import SubscriptionCard from "@/components/subscription/SubscriptionCard";
import { toast } from "@/components/ui/use-toast";
import { PendingEmailBanner } from "@/components/auth/PendingEmailBanner";
import TourWrapper from "@/components/onboarding/TourWrapper";
import { buyerProfileSteps, sellerProfileSteps } from "@/config/tours";
import { safeStorage } from "@/utils/safariStorage";

const ProfilePage = () => {
  const { buyerProfile, isLoading: buyerLoading } = useBuyer();
  const { profile, isLoading: authLoading } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [runTour, setRunTour] = useState(false);

  // Determine profile type based on route context
  const isBuyerRoute = location.pathname.startsWith('/buyer');
  const isSellerRoute = location.pathname.startsWith('/seller');

  // Check for password reset parameter and show dialog
  useEffect(() => {
    const resetParam = searchParams.get('reset');
    if (resetParam === 'true') {
      setShowPasswordDialog(true);
      // Remove the reset parameter from URL
      searchParams.delete('reset');
      setSearchParams(searchParams, { replace: true });
      
      toast({
        title: "Password Reset",
        description: "You've been automatically logged in. Please change your password below.",
      });
    }
  }, [searchParams, setSearchParams]);

  // Tour initialization
  useEffect(() => {
    const tourId = isBuyerRoute ? 'buyer_profile' : 'seller_profile';
    const hasSeenTour = safeStorage.getItem(`godyspo_tour_${tourId}`) === 'true';
    const shouldStartTour = (location.state as { startTour?: string })?.startTour === tourId;
    
    if (!hasSeenTour || shouldStartTour) {
      const checkAndStartTour = (retries = 5) => {
        const tabs = document.querySelector('[data-tour="profile-tabs"]');
        if (tabs) {
          setTimeout(() => setRunTour(true), 300);
        } else if (retries > 0) {
          setTimeout(() => checkAndStartTour(retries - 1), 200);
        }
      };
      
      const timer = setTimeout(() => checkAndStartTour(), 500);
      return () => clearTimeout(timer);
    }
  }, [isBuyerRoute, location.state]);

  const handleTourComplete = () => {
    const tourId = isBuyerRoute ? 'buyer_profile' : 'seller_profile';
    safeStorage.setItem(`godyspo_tour_${tourId}`, 'true');
    setRunTour(false);
  };

  const handleTourSkip = () => {
    const tourId = isBuyerRoute ? 'buyer_profile' : 'seller_profile';
    safeStorage.setItem(`godyspo_tour_${tourId}`, 'true');
    setRunTour(false);
  };

  // Show loading state while data is being fetched
  const isLoading = authLoading || buyerLoading;
  
  if (isLoading) {
    return (
      <div className="space-y-6 pb-8 animate-fade-in-stable">
        <div>
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-96" />
        </div>
        
        <div className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  // Debug logging to understand the state
  console.log("ProfilePage Debug:", {
    buyerProfile: !!buyerProfile,
    sellerProfile: !!profile,
    buyerLoading,
    authLoading,
    isLoading,
    currentPath: location.pathname
  });

  // Check if user has both profiles (dual-profile scenario)
  const hasBothProfiles = Boolean(buyerProfile && profile);
  
  // Handle dual-profile scenario
  if (hasBothProfiles) {
    // If on a specific route, prioritize that profile type
    if (isBuyerRoute) {
      return renderBuyerProfile();
    } else if (isSellerRoute) {
      return renderSellerProfile();
    } else {
      // If on a neutral route, show profile type selector
      return <ProfileTypeSelector />;
    }
  }

  // Single profile scenarios
  if (buyerProfile && !profile) {
    return renderBuyerProfile();
  }

  if (profile && !buyerProfile) {
    return renderSellerProfile();
  }

  // No profile found - this shouldn't happen for authenticated users
  return (
    <div className="space-y-6 pb-8 animate-fade-in-stable">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Profile Setup Required</h1>
        <p className="text-muted-foreground">
          We couldn't find your profile. Please contact support or try refreshing the page.
        </p>
      </div>
      
      <div className="bg-muted/50 p-6 rounded-lg">
        <p className="text-sm text-muted-foreground mb-4">
          If this message persists, try refreshing the page or contact support.
        </p>
        <div className="text-xs text-muted-foreground/70">
          Debug: Buyer={buyerProfile ? 'Yes' : 'No'}, Seller={profile ? 'Yes' : 'No'}
        </div>
      </div>
    </div>
  );

  function renderBuyerProfile() {
    return (
      <>
        <div className="space-y-6 pb-8 animate-fade-in-stable">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">My Profile</h1>
            <p className="text-muted-foreground">
              Manage your buyer account information and investment preferences
            </p>
          </div>

          {/* Email Verification Banner */}
          <PendingEmailBanner />
          
          <Tabs defaultValue="personal" className="w-full">
          <TabsList data-tour="profile-tabs" className="w-full flex overflow-x-auto">
            <TabsTrigger value="personal" className="flex-shrink-0">Personal Info</TabsTrigger>
            <TabsTrigger value="preferences" className="flex-shrink-0">Preferences</TabsTrigger>
            <TabsTrigger value="subscription" className="flex-shrink-0">Subscription</TabsTrigger>
            <TabsTrigger value="billing" className="flex-shrink-0">Billing</TabsTrigger>
          </TabsList>
            
            <TabsContent value="personal" className="mt-6 space-y-6">
              {/* Buyer Profile Image */}
              <div data-tour="profile-image">
                <BuyerProfileImageSection />
              </div>
              
              {/* Buyer Personal Info Form */}
              <div data-tour="personal-info">
                <BuyerPersonalInfoForm />
              </div>
              
              {/* Security Section */}
              <div data-tour="security-section">
                <SecuritySection />
              </div>
            </TabsContent>
            
            <TabsContent value="preferences" className="mt-6">
              <BuyerInvestmentPreferencesForm />
            </TabsContent>
            
            <TabsContent value="subscription" className="mt-6">
              <SubscriptionCard userType="buyer" showUpgradeOptions={true} />
            </TabsContent>

            <TabsContent value="billing" className="mt-6">
              <BillingTab />
            </TabsContent>
          </Tabs>
        </div>

        <ChangePasswordDialog
          open={showPasswordDialog}
          onOpenChange={setShowPasswordDialog}
          isPasswordReset={true}
          redirectTo="/buyer/login"
        />

        <TourWrapper
          steps={buyerProfileSteps}
          run={runTour}
          onComplete={handleTourComplete}
          onSkip={handleTourSkip}
        />
      </>
    );
  }

  function renderSellerProfile() {
    return (
      <>
        <div className="space-y-6 pb-8 animate-fade-in-stable">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">My Profile</h1>
            <p className="text-muted-foreground">
              Manage your account information and preferences
            </p>
          </div>

          {/* Email Verification Banner */}
          <PendingEmailBanner />
          
          <Tabs defaultValue="personal" className="w-full">
            <TabsList data-tour="profile-tabs" className="w-full flex overflow-x-auto">
              <TabsTrigger value="personal" className="flex-shrink-0">Personal Info</TabsTrigger>
              <TabsTrigger value="markets" className="flex-shrink-0">Markets</TabsTrigger>
              <TabsTrigger value="subscription" className="flex-shrink-0">Subscription</TabsTrigger>
              <TabsTrigger value="billing" className="flex-shrink-0">Billing</TabsTrigger>
            </TabsList>
            
            <TabsContent value="personal" className="mt-6 space-y-6">
              {/* Profile Image */}
              <div data-tour="profile-image">
                <ProfileImageSection />
              </div>
              
              {/* Profile Details Form */}
              <div data-tour="personal-info">
                <PersonalInfoForm />
              </div>
              
              {/* Security Section */}
              <div data-tour="security-section">
                <SecuritySection />
              </div>
            </TabsContent>
            
            <TabsContent value="markets" className="mt-6">
              <MarketsTab />
            </TabsContent>
            
            <TabsContent value="subscription" className="mt-6">
              <SubscriptionCard userType="seller" showUpgradeOptions={true} />
            </TabsContent>
            
            <TabsContent value="billing" className="mt-6">
              <BillingTab />
            </TabsContent>
          </Tabs>
        </div>

        <ChangePasswordDialog
          open={showPasswordDialog}
          onOpenChange={setShowPasswordDialog}
          isPasswordReset={true}
          redirectTo="/seller/login"
        />

        <TourWrapper
          steps={sellerProfileSteps}
          run={runTour}
          onComplete={handleTourComplete}
          onSkip={handleTourSkip}
        />
      </>
    );
  }
};

export default ProfilePage;
